import React, { useCallback, useEffect, useState } from 'react';
import styled from 'astroturf/react';
import { Icon } from 'js/design_system/Icon';
import {
  Popover,
  PopoverMode,
  PopoverWidthMode,
} from 'js/design_system/Popover';
import * as arbitraryEvents from 'js/core_webpack/arbitrary_events';

interface SortOption {
  value: string;
  label: string;
}

interface ArticlesSortProps {
  sortOptions: SortOption[];
  selectedSort: string;
  journalId?: number | string | null;
  pageType?: 'single_journal' | 'all_journals';
}

const SortButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  font-family: var(--type-font-family-sans-serif, Roboto), sans-serif;
  font-size: var(--type-body-md-size, 16px);
  font-weight: var(--type-body-md-font-weight, 400);
  line-height: var(--type-body-md-line-height, 20px);
  color: var(--text-primary, #070a15);
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--text-hover, #0c3b8d);
  }

  &:active {
    color: var(--text-press, #082f75);
  }
`;

const SortMenu = styled.div`
  background: var(--background-white, #fff);
  border-radius: 4px;
  border: 1px solid var(--hover, #0c3b8d);
  padding: 8px 0;
`;

const SortMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 8px 16px;
  margin: 0;
  border: 1px solid transparent;
  background: var(--background-white, #fff);
  font-family: var(--type-font-family-sans-serif, Roboto), sans-serif;
  font-size: var(--type-body-md-size, 16px);
  font-weight: var(--type-body-md-font-weight, 400);
  line-height: var(--type-body-md-line-height, 20px);
  color: var(--text-primary, #070a15);
  cursor: pointer;
  text-align: left;
  white-space: nowrap;

  &:hover {
    color: var(--text-hover, #0c3b8d);
  }

  &:active {
    color: var(--text-press, #082f75);
  }

  &:focus:not(:active) {
    outline: none;
    border: 1px solid var(--buttons-button-focus, #0645b1);
  }

  &:focus:not(:active):not(:focus-visible) {
    outline: none;
    border: 1px solid transparent;
  }
`;

const ArticlesSort: React.FC<ArticlesSortProps> = ({
  sortOptions,
  selectedSort,
  journalId,
  pageType = 'all_journals',
}) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;

    const handleScroll = () => {
      setOpen(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [open]);

  const handleSortChange = useCallback(
    (option: SortOption) => {
      // Track the sort change event
      arbitraryEvents.write('JournalArticlesSortEvent', {
        journal_id:
          typeof journalId === 'string' ? parseInt(journalId, 10) : journalId,
        sort_value: option.value,
        previous_sort: selectedSort,
        page_type: pageType,
      });

      const params = new URLSearchParams(window.location.search);
      if (option.value === 'publication_date') {
        params.delete('sort');
      } else {
        params.set('sort', option.value);
      }
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.location.href = newUrl;
    },
    [journalId, selectedSort, pageType],
  );

  const selectedOption =
    sortOptions.find((opt) => opt.value === selectedSort) || sortOptions[0];

  return (
    <Popover
      open={open}
      mode={PopoverMode.Click}
      widthMode={PopoverWidthMode.Content}
      placement="bottom-end"
      onChange={setOpen}
      render={() => (
        <SortMenu role="menu" aria-label="Sort options">
          {sortOptions.map((option) => (
            <SortMenuItem
              key={option.value}
              role="menuitem"
              aria-current={option.value === selectedSort ? 'true' : undefined}
              onClick={() => handleSortChange(option)}
            >
              {option.label}
            </SortMenuItem>
          ))}
        </SortMenu>
      )}
    >
      <SortButton
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Sort by: ${selectedOption.label}`}
      >
        {selectedOption.label}
        <Icon name="keyboard_arrow_down" size={20} aria-hidden="true" />
      </SortButton>
    </Popover>
  );
};

export default ArticlesSort;
